import * as Sentry from '@sentry/react'

import React from 'react'

import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom'

import { isAxiosError } from 'axios'

import { normalizedEnv, runtime } from '@/config/env'

/**
 * Simple Sentry configuration without replay integration
 * Replay removed to reduce bundle size by ~150KB
 */

const SENTRY_DSN = normalizedEnv.sentryDsn
const SENTRY_ENVIRONMENT = normalizedEnv.sentryEnvironment || runtime.mode || 'development'
const ENABLE_SENTRY = normalizedEnv.enableSentry

/**
 * Initialize Sentry
 * Call this in main.tsx before rendering React
 */
export function initSentry() {
  if (!ENABLE_SENTRY || !SENTRY_DSN) {
    console.info('[Tracer] Disabled or not configured')
    return
  }

  try {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: SENTRY_ENVIRONMENT,

      // Essential integrations only - replay removed to save ~150KB
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.reactRouterV7BrowserTracingIntegration({
          useEffect: React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
      ],

      tracesSampleRate: runtime.isProd ? 0.1 : 1.0,
      maxBreadcrumbs: 50,

      // Filter out expected errors
      beforeSend(event, hint) {
        const error = hint.originalException

        // Filter AxiosError 401/422
        if (isAxiosError(error)) {
          const status = error.response?.status
          if (status === 401 || status === 422) {
            return null
          }
        }

        if (error && typeof error === 'object' && 'message' in error) {
          const message = String(error.message).toLowerCase()
          if (
            message.includes('network error') ||
            message.includes('failed to fetch') ||
            message.includes('load failed')
          ) {
            return null
          }
        }

        return event
      },
    })

    console.info(`[Tracer] Initialized for ${SENTRY_ENVIRONMENT}`)
  } catch (error) {
    console.error('[Tracer] Failed to initialize')
  }
}

/**
 * Capture exception in Sentry
 * Used by your existing logger in lib/logger.ts
 */
export function captureException(error: any, context?: Sentry.CaptureContext) {
  if (!ENABLE_SENTRY) return
  Sentry.captureException(error, context)
}

/**
 * Track performance spans for API calls, button clicks, etc.
 */
export function startSpan<T>(context: Parameters<typeof Sentry.startSpan>[0], callback: (span: Sentry.Span) => T): T {
  if (!ENABLE_SENTRY) {
    return callback({} as any)
  }
  return Sentry.startSpan(context, callback)
}

/**
 * Set user context after login
 */
export function setUserContext(user: { id: string | number; email?: string; [key: string]: any }) {
  if (!ENABLE_SENTRY) return
  Sentry.setUser(user)
}

/**
 * Clear user context on logout
 */
export function clearUserContext() {
  if (!ENABLE_SENTRY) return
  Sentry.setUser(null)
}

export { Sentry }
